import {Component, Input, OnInit} from '@angular/core';
import {OfferSetSharingRuleModel} from '../../../../../model/offer-set-sharing-rule.model';
import {DialogService} from 'primeng/dynamicdialog';
import {SharedOfferSetRulesEntryComponent} from '../shared-offer-set-rules-entry/shared-offer-set-rules-entry.component';
import {ConfirmationService, MessageService} from 'primeng/api';
import {SharedOfferSetRuleService} from '../../../../../service/shared-offer-set-rule.service';
import {OfferSetSharingRuleFilterModel} from '../../../../../model/offer-set-sharing-rule-filter.model';
import {EventEmitterService} from '../../../../../service/event-emitter.service';
import {MakeModel} from '../../../../../model/make.model';
import {DashboardAccountInterface} from '../../../../../model/dashboard-account.interface';
import {AccountService} from '../../../../../service/account.service';
import {SharedOfferSetRulesPreviewComponent} from '../shared-offer-set-rules-entry/shared-offer-set-rules-preview/shared-offer-set-rules-preview.component';
import {SharingRuleFilterModel} from '../../../../../model/sharing-rule-filter.model';

@Component({
  selector: 'auto-shared-offer-set-rules-table',
  templateUrl: './shared-offer-set-rules-table.component.html',
  styleUrl: './shared-offer-set-rules-table.component.scss',
  providers: [DialogService]
})
export class SharedOfferSetRulesTableComponent implements OnInit {
  @Input() sharedOfferSetsRules: OfferSetSharingRuleModel[] = [];
  @Input() sourceAccounts: any[] = [];
  @Input() makes: MakeModel[] = [];
  sourceAccountOptions: any[] = [];
  targetAccountOptions: any[] = [];
  filterDefinitionMap = [];
  accountFilters: any[] = [];
  offerFilters: any[] = [];
  searchValue: string;
  globalFilterFields = ['ruleName', 'sourceAccount.name'];
  filterDefinitions = [];
  showMoreMapAccount: { [key: string]: boolean } = {};
  showMoreMapOfferSet: { [key: string]: boolean } = {};
  tagsPerRow = 2;
  sourceAccountsLoading = true;

  constructor(
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private accountService: AccountService,
    private eventEmitterService: EventEmitterService,
    private sharedOfferSetRuleService: SharedOfferSetRuleService
  ) {
  }

  ngOnInit(): void {
    this.sharedOfferSetRuleService.getOfferSetSharingRuleFilterDefinition().subscribe({
      next: (filters) => {
        this.filterDefinitions = filters;
        filters.forEach(
          (filterDefinition) => {
            this.filterDefinitionMap[filterDefinition.id] = filterDefinition.filterName;
          }
        );
        this.accountFilters = filters
          .filter((filter) => filter.filterType === 'account' && filter.filterName !== 'Make')
          .map((filter) => ({
            label: filter.filterName = this.getFilterName(filter),
            value: filter.id,
            type: filter.dataType
          }))
          .sort((firstElement, secondElement) => firstElement.label.localeCompare(secondElement.label));

        this.offerFilters = filters
          .filter((filter) => filter.filterType === 'offerSet' && filter.filterName !== 'Make')
          .map((filter) => ({label: filter.filterName, value: filter.id, type: filter.dataType}))
          .sort((firstElement, secondElement) => firstElement.label.localeCompare(secondElement.label));
        this.loadAccountData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load filter definitions'
        });
      }
    });
  }

  /**
   * Open the dialog to create a new rule
   */
  addRule() {
    this.dialogService.open(SharedOfferSetRulesEntryComponent, {
      header: 'Create Shared Offer Set Rule',
      width: '80vw',
      closable: false,
      modal: true,
      styleClass: 'shared-offer-set-rules-dialog',
      data: {
        sharedOfferSetRules: this.sharedOfferSetsRules,
        openedModal: 'add',
        sourceAccountOptions: this.sourceAccountOptions,
        targetAccountOptions: this.targetAccountOptions,
        filterDefinitionMap: this.filterDefinitionMap,
        filterDefinitions: this.filterDefinitions,
        accountFilters: this.accountFilters,
        offerFilters: this.offerFilters,
        makes: this.makes
      }
    });
  }

  /**
   * Open the dialog to edit an existing rule
   */
  editRule(rule: OfferSetSharingRuleModel) {
    this.dialogService.open(SharedOfferSetRulesEntryComponent, {
      header: 'Edit Shared Offer Set Rule',
      width: '80vw',
      modal: true,
      closable: false,
      styleClass: 'shared-offer-set-rules-dialog',
      data: {
        rule: rule,
        sharedOfferSetRules: this.sharedOfferSetsRules,
        sourceAccountOptions: this.sourceAccountOptions,
        targetAccountOptions: this.targetAccountOptions,
        filterDefinitionMap: this.filterDefinitionMap,
        filterDefinitions: this.filterDefinitions,
        accountFilters: this.accountFilters,
        offerFilters: this.offerFilters,
        openedModal: 'edit',
        makes: this.makes
      }
    });
  }

  /**
   * Method to preview rule
   */
  previewRule(rule: any) {
    const appliedFiltersRequest = this.getAppliedFilters(rule);
    this.sharedOfferSetRuleService.getSharedOfferSetRuleFilter(appliedFiltersRequest).subscribe(
      {
        next: (appliedFilters) => {
          this.dialogService.open(SharedOfferSetRulesPreviewComponent, {
            header: 'Preview ' + rule.ruleName + ' Rule',
            modal: true,
            width: '40vw',
            closable: true,
            data: {
              appliedFilters: appliedFilters,
            }
          });
        },
        error: (error) => {
          let message = 'Failed to get preview for filters.';
          if (error.violations && error.violations.length > 0) {
            message = error.violations[0].message;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: message
          });
        }
      }
    );

  }

  /**
   * Load the account data from the service
   */
  loadAccountData() {
    this.accountService.getDashboardAccounts(true).subscribe({
      next: (accounts: DashboardAccountInterface[]) => {
        this.sourceAccountOptions = accounts
          .map((account) => ({label: account.name, value: account.accountId}));
        this.sourceAccountOptions.sort((firstItem, secondItem) => firstItem.label.localeCompare(secondItem.label));
        this.targetAccountOptions = accounts
          .filter((account) => account.sharedOfferSetsEnabled)
          .map((account) => ({label: account.name, value: account.accountId}));
        this.targetAccountOptions.sort((firstItem, secondItem) => firstItem.label.localeCompare(secondItem.label));
        this.sourceAccountsLoading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load MPOP accounts.'
        });
      }
    });
  }

  /**
   * Delete a Offer set sharing rule
   */
  deleteRule(event: Event, rule: OfferSetSharingRuleModel) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to delete this rule and all associated filters?',
      header: 'Confirm Deletion of Rule: ' + (rule.ruleName),
      icon: 'pi pi-info-circle',
      acceptLabel: 'Delete',
      acceptButtonStyleClass: 'p-button-danger',
      rejectLabel: 'Cancel',
      accept: () => {
        this.sharedOfferSetRuleService.deleteSharedOfferSetRule(rule.id).subscribe(
          () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Rule Deleted'
            });
            this.sharedOfferSetsRules = this.sharedOfferSetsRules.filter(r => r.id !== rule.id);
            this.eventEmitterService.emitEvent('loadSharedOfferSetRules');
          },
          error => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete rule'
            });
          }
        );
      }
    });
  }


  /**
   * Get the filter segments for account and offer set filters
   */
  getFilterSegments(rule: OfferSetSharingRuleModel, ruleIndex: number, filterType: string): OfferSetSharingRuleFilterModel[] {
    const filters = this.getFilters(rule, filterType);
    let filterSegments = filters.slice(0, this.tagsPerRow);
    if ((filterType === 'account' && this.showMoreMapAccount[ruleIndex])
      || (filterType === 'offerSet' && this.showMoreMapOfferSet[ruleIndex]))
    {
      filterSegments = filters;
    }

    return filterSegments;
  }

  /**
   * Get the filters of a specific type (account or offer set) from the rule
   */
  getFilters(rule: OfferSetSharingRuleModel, filterType: string) {
    return rule.offerSetSharingRuleFilters.filter((filter) => {
      return filter.filterType === filterType;
    });
  }

  /**
   * Toggle the visibility of the "Show More" button for account and offer set filters
   */
  toggleShowMore(ruleIndex: number, filterType: string) {
    if (filterType === 'account') {
      this.showMoreMapAccount[ruleIndex] = !this.showMoreMapAccount[ruleIndex];
    } else if (filterType === 'offerSet') {
      this.showMoreMapOfferSet[ruleIndex] = !this.showMoreMapOfferSet[ruleIndex];
    }
  }

  /**
   * Get the text for the Show More/Less button
   */
  getShowMoreLessText(rule: OfferSetSharingRuleModel, ruleIndex: number, filterType: string) {
    let showText = 'Show Less';
    if ((filterType === 'account' && !this.showMoreMapAccount[ruleIndex]) ||
        (filterType === 'offerSet' && !this.showMoreMapOfferSet[ruleIndex])) {
      showText = '+' + (this.getFilters(rule, filterType).length - this.tagsPerRow) + ' More Filters';
    }
    return showText;
  }

  /**
   * Handle the row click event
   */
  onRowClick(rule: any) {
    this.editRule(rule);
  }

  /**
   * Method to create sharing rule filter request
   */
  getAppliedFilters(rule: OfferSetSharingRuleModel) {
    let sharingRuleFilterRequest = new SharingRuleFilterModel();
    sharingRuleFilterRequest.sourceAccountId = rule.sourceAccount['id'];
    rule.offerSetSharingRuleFilters.forEach((filter) => {
      if (Array.isArray(filter.value)) {
        filter.value = filter.value.join(',');
      }
      sharingRuleFilterRequest.offerSetSharingRuleFilters.push(
        {
          filterName: this.filterDefinitionMap[filter.offerSetSharingRuleFilterDefinitionId],
          filterType: filter.filterType,
          operator: filter.operator,
          value: filter.value
        }
      );
    });

    return sharingRuleFilterRequest;
  }

  /**
   * Method to get the filter name and return MPOP Account if the option selected is Account ID
   */
   getFilterName(filter): string {
    let result = filter.filterName;
    if (filter.filterName === 'Account ID') {
      result = 'MPOP Account';
    }
      return result;
    }
}
